/**
 * The production's devices (PLAN §7.2, D25's device panel): a chip per
 * device, the unhealthy first, and a one-line summary — so a device going
 * bad mid-show is the first thing in the panel, not the fifth.
 */
import type { DeviceStatusWire } from '@acp/api-contract/playout.js';
import { DeviceStatusChip } from './DeviceStatusChip.js';

export type DevicePanelProps = {
  readonly devices: readonly DeviceStatusWire[];
  /** The live state has not arrived (or cannot): the devices are unknown, not fine. */
  readonly available: boolean;
  readonly nowMs: number;
};

const RANK: Readonly<Record<string, number>> = { disconnected: 0, bad: 1, warning: 2, unknown: 3, good: 4 };

function rankOf(d: DeviceStatusWire): number {
  return RANK[d.connected ? d.status : 'disconnected'] ?? 5;
}

export function DevicePanel({ devices, available, nowMs }: DevicePanelProps) {
  if (!available) {
    return (
      <p className="text-sm text-muted-foreground" data-testid="devices-unavailable">
        Device status arrives with the live playout state.
      </p>
    );
  }
  if (devices.length === 0) {
    return (
      <p className="text-sm text-muted-foreground" data-testid="devices-empty">
        This production’s studio config has no devices.
      </p>
    );
  }
  const sorted = [...devices].sort((a, b) => rankOf(a) - rankOf(b) || a.deviceId.localeCompare(b.deviceId));
  const unhealthy = devices.filter((d) => !d.connected || d.status === 'bad' || d.status === 'warning').length;
  return (
    <div className="grid gap-2" data-testid="device-panel">
      <p className="text-sm" data-testid="devices-summary">
        {unhealthy === 0 ? `${String(devices.length)} device${devices.length === 1 ? '' : 's'}, none reporting a problem` : `${String(unhealthy)} of ${String(devices.length)} need attention`}
      </p>
      {sorted.map((d) => (
        <DeviceStatusChip key={d.deviceId} device={d} nowMs={nowMs} />
      ))}
    </div>
  );
}
